import { useState } from "react";
import { AxiosError, AxiosResponse } from "axios";
import { IdNameList } from "../common/models";
import { useMutation, useQuery } from "@tanstack/react-query";
import { API_ENDPOINTS } from "../../../helpers/constants";
import apiInstance from "../../instances/apiInstance";
import {
  CreateUserError,
  CreateUserParams,
  CreateUserResponse,
  MarketSearchResponse,
  ServiceListParams,
  TimeZoneListParams,
  UserEmailVerificationParams,
  UsersExportParams,
  UsersSearchParams,
} from "./models";
import msGraphAxiosInstance from "../../instances/msGraphAxiosInstance";
import { isNotEmpty } from "../../../helpers/utils";

export const useGetManageUserRolesList = () => {
  return useQuery({
    queryKey: ["getManageUserRolesList"],
    queryFn: async () => {
      const res = await apiInstance.get(API_ENDPOINTS.GET_USER_ROLES);
      return res.data;
    },
    retry: false,
  });
};

export const useGetSearchUserList = () => {
  return useMutation({
    mutationFn: async (params: UsersSearchParams) => {
      const response = await apiInstance.post(
        API_ENDPOINTS.GET_SEARCH_USER_LIST,
        params,
      );
      return response.data;
    },
  });
};

export const useGetManageUserVerifyEmailIdFromADQuery = () => {
  const [params, setParams] = useState<UserEmailVerificationParams>();

  const query = useQuery({
    queryKey: ["getManageUserVerifyEmailIdFromADQuery", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const { email = "" } = params;
      const res = await msGraphAxiosInstance.get(
        API_ENDPOINTS.GET_VERIFY_USER_EMAIL_ID_FROM_AD(email),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: UserEmailVerificationParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useGetUserCronofyServiceAccountDataQuery = () => {
  return useQuery({
    queryKey: ["getCronofyServiceAccountDataQuery"],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_USER_SERVICE_ACCOUNT_LIST,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetUserTimeZoneListForAdminUserQuery = () => {
  return useQuery({
    queryKey: ["useGetUserTimeZoneListForAdminUserQuery"],
    queryFn: async () => {
      const res = await apiInstance.get(API_ENDPOINTS.GET_USER_TIMEZONE_LIST);
      return !!res.data[0] ? res.data[0].items : [];
    },
    retry: false,
  });
};

export const useCreateUserMutation = () => {
  return useMutation<
    CreateUserResponse,
    AxiosError<CreateUserError>,
    CreateUserParams
  >({
    mutationFn: async (params: CreateUserParams) => {
      const response = await apiInstance.post(
        API_ENDPOINTS.CREATE_USER,
        params,
      );
      return response.data;
    },
  });
};

export const useGetMarketSearchListMutation = () => {
  return useMutation<MarketSearchResponse[], unknown, string[] | undefined>({
    mutationFn: async (regionIds?: string[]) => {
      const params = isNotEmpty(regionIds)
        ? { regionid: regionIds.join(",") }
        : undefined;

      const response = await apiInstance.post<MarketSearchResponse[]>(
        API_ENDPOINTS.GET_MARKET_SEARCH,
        { params },
      );

      return response.data;
    },
  });
};

export const useEditUserMutation = () => {
  return useMutation<
    CreateUserResponse,
    AxiosError<CreateUserError>,
    CreateUserParams
  >({
    mutationFn: async (params: CreateUserParams) => {
      const response = await apiInstance.put(API_ENDPOINTS.EDIT_USER, params);
      return response.data;
    },
  });
};

export const useGetUserListById = () => {
  const [params, setParams] = useState<string>("");
  const query = useQuery({
    queryKey: ["useGetUserListById", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }

      const res = await apiInstance.get(API_ENDPOINTS.getUserListById(params));
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
  });

  const refetchWithParams = (newParams: string) => {
    setParams(newParams);
  };

  return { ...query, refetchWithParams };
};

export const useGetCronofyTimeZoneList = () => {
  const [params, setParams] = useState<TimeZoneListParams>();
  const query = useQuery({
    queryKey: ["getCronofyTimeZoneList", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }

      const { countryCode = "" } = params;
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_CRONOFY_TIMEZONE_LIST(countryCode),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: TimeZoneListParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useGetManageUserListFromADQuery = () => {
  const [params, setParams] = useState<UserEmailVerificationParams>();

  const query = useQuery({
    queryKey: ["getManageUserListFromADQuery", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const { email = "" } = params;
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_USER_LIST_FROM_AD(email),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: UserEmailVerificationParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useExportUserReport = () => {
  return useMutation<AxiosResponse<Blob>, Error, UsersExportParams>({
    mutationFn: (params: UsersExportParams): Promise<AxiosResponse<Blob>> => {
      return apiInstance.post(
        API_ENDPOINTS.EXPORT_USER,
        {
          ...params,
        },
        {
          responseType: "blob",
        },
      );
    },
  });
};

export const useGetDateFormatMasterList = () => {
  return useQuery({
    queryKey: ["getDateFormatMasterList"],
    queryFn: async (): Promise<IdNameList[]> => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_DATE_FORMAT_MASTER_LIST,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetServiceOfficeListByCountryCodesQuery = () => {
  const [params, setParams] = useState<ServiceListParams>();

  const query = useQuery({
    queryKey: ["getServiceOfficeListByCountryCodesQuery", params?.countryCode],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const { countryCode = "" } = params;
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_USER_SERVICE_LIST_BY_COUNTRY_IDS(countryCode),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: ServiceListParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};
